//! Log Service - Tauri Commands Integration

import type { LogEntry, LogFilter, LogStoreStats, ScopeFilter } from "../entities";

import { core } from "@tauri-apps/api";

export interface GetLogsRequest extends Record<string, unknown> {
  filter: LogFilter;
  limit?: number;
  scope_filter?: ScopeFilter;
}

export interface GetLogsResponse {
  logs: LogEntry[];
  returned_count: number;
  total_count: number;
}

export interface ClearLogsRequest extends Record<string, unknown> {
  domain?: string;
}

export interface ClearLogsResponse {
  deleted_count: number;
  success: boolean;
}

export interface UpdateConfigRequest extends Record<string, unknown> {
  collector_config?: Record<string, unknown>;
  store_config?: Record<string, unknown>;
}

export interface UpdateConfigResponse {
  message: string;
  success: boolean;
}

/**
 * Log Service - Zentrale Log-Verwaltung
 *
 * Stellt alle Tauri Commands für das Debug-System bereit.
 */
export const LogService = {
  /**
   * Holt Logs basierend auf Filter
   *
   * @param request - Request parameters for getting logs
   *
   * @returns Response with logs and metadata
   *
   */
  async getLogs(request: GetLogsRequest): Promise<GetLogsResponse> {
    return await core.invoke("get_logs", request);
  },

  /**
   * Löscht alle Logs oder Domain-spezifische Logs
   *
   * @param request - Optional request to clear specific domain logs
   *
   * @returns Response with deletion count and success status
   *
   */
  async clearLogs(request?: ClearLogsRequest): Promise<ClearLogsResponse> {
    return await core.invoke("clear_logs", request ?? {});
  },

  /**
   * Holt Log Store Statistics
   *
   * @returns Log store statistics
   *
   */
  async getStats(): Promise<LogStoreStats> {
    const response = await core.invoke<{ stats: LogStoreStats }>("get_log_stats");
    return response.stats;
  },

  /**
   * Aktualisiert Log-Konfiguration
   *
   * @param request - Configuration update request
   *
   * @returns Response with success status and message
   *
   */
  async updateConfig(request: UpdateConfigRequest): Promise<UpdateConfigResponse> {
    return await core.invoke("update_log_config", request);
  },

  /**
   * Holt verfügbare Domains
   *
   * @returns Array of available domain names
   *
   */
  async getAvailableDomains(): Promise<string[]> {
    return await core.invoke("get_available_domains");
  },

  /**
   * Holt verfügbare Module für eine Domain
   *
   * @param domain - Domain name to get modules for
   *
   * @returns Array of available module names
   *
   */
  async getAvailableModules(domain: string): Promise<string[]> {
    return await core.invoke("get_available_modules", { domain });
  },

  /**
   * Holt verfügbare Files für ein Modul
   *
   * @param domain - Domain name
   *
   * @param module - Module name
   *
   * @returns Array of available file names
   *
   */
  async getAvailableFiles(domain: string, module: string): Promise<string[]> {
    return await core.invoke("get_available_files", { domain, module });
  },

  /**
   * Holt verfügbare Functions für eine Datei
   *
   * @param domain - Domain name
   *
   * @param module - Module name
   *
   * @param file - File name
   *
   * @returns Array of available function names
   *
   */
  async getAvailableFunctions(domain: string, module: string, file: string): Promise<string[]> {
    return await core.invoke("get_available_functions", {
      domain,
      file,
      module,
    });
  },

  /**
   * Exportiert Logs
   *
   * @param format - Export format (json, csv, text, logstash)
   *
   * @param filter - Filter criteria for logs to export
   *
   * @param filePath - Path to export file
   *
   * @param prettyPrint - Whether to format output
   *
   * @returns Export result
   *
   */
  async exportLogs(
    format: string,
    filter: LogFilter,
    filePath: string,
    prettyPrint = true,
  ): Promise<unknown> {
    return await core.invoke("export_logs", {
      file_path: filePath,
      filter,
      format,
      pretty_print: prettyPrint,
    });
  },
};
